import {
  type ActivityMetadata,
  type ActivityProfileModule,
  type ActivityRendererFamily,
  type ActivityResultSchema,
  type ActivitySessionRules,
  DEFAULT_ACTIVITY_SESSION_CONFIG,
} from "@/db/schema/activities";

const SESSION_ONLY_ACTIVITY_IDS = new Set([
  "running",
  "walking",
  "cycling",
  "swimming",
  "weighttraining",
  "yoga",
  "pilates",
  "recovery",
  "hyrox",
  "golf",
  "climbing",
  "dayretreats",
  "mixedmartialarts",
  "hiit",
  "hotyoga",
  "nutrition",
  "scubadiving",
  "snorkeling",
  "trekking",
  "animalflow",
  "bouldering",
  "stretching",
]);

const COURT_MATCH_ACTIVITY_IDS = new Set([
  "tennis",
  "badminton",
  "tabletennis",
  "squash",
  "padel",
  "pickleball",
]);

const TEAM_MATCH_ACTIVITY_IDS = new Set([
  "football",
  "basketball",
  "volleyball",
  "throwball",
  "cricket",
]);

const SPECIALTY_MATCH_ACTIVITY_IDS = new Set(["snooker"]);

const TEMPLATE_KEY_OVERRIDES: Record<string, string> = {
  dayretreats: "day-retreats",
  mixedmartialarts: "mixed-martial-arts",
  tabletennis: "table-tennis",
  weighttraining: "weight-training",
  hotyoga: "hot-yoga",
  scubadiving: "scuba-diving",
  animalflow: "animal-flow",
};

const DEFAULT_RULES: ActivitySessionRules = {
  archetype: "session_only",
  session: {
    joinable: true,
    formats: ["group"],
    defaultFormat: "group",
    participantRange: {
      group: { min: 1, max: 50 },
    },
    supportsJoinApproval: true,
    supportsInviteCode: true,
    showsInFeed: true,
    showsInDiscover: true,
  },
  competition: {
    allowed: false,
    modes: [],
    defaultMode: null,
  },
  result: {
    allowed: false,
    optional: false,
    schema: "none",
    verification: "none",
    retroResultAllowed: false,
    affectsRatingWhen: "never",
  },
  renderer: {
    family: "group_session",
    templateKey: null,
    scoreEditorKey: null,
    scorecardKey: null,
  },
  profile: {
    modules: ["sessions", "attendance"],
  },
};

const toNormalizedActivityId = (activityId: string) =>
  activityId
    .toLowerCase()
    .replace(/[^a-z-]/g, "")
    .replace(/-/g, "");

export function getActivityTemplateKey(activityId: string): string {
  const normalizedId = toNormalizedActivityId(activityId);
  return TEMPLATE_KEY_OVERRIDES[normalizedId] ?? activityId;
}

export function getActivityRendererFamily(
  activityId: string,
): ActivityRendererFamily {
  const normalizedId = toNormalizedActivityId(activityId);

  if (COURT_MATCH_ACTIVITY_IDS.has(normalizedId)) {
    return "court_match";
  }

  if (TEAM_MATCH_ACTIVITY_IDS.has(normalizedId)) {
    return "team_match";
  }

  if (SPECIALTY_MATCH_ACTIVITY_IDS.has(normalizedId)) {
    return "specialty_match";
  }

  return "group_session";
}

function getProfileModules(archetype: ActivitySessionRules["archetype"]) {
  const baseModules: ActivityProfileModule[] = ["sessions", "attendance"];

  if (archetype === "scored_match") {
    return [
      ...baseModules,
      "verified_results",
      "rating",
      "record",
    ] satisfies ActivityProfileModule[];
  }

  return baseModules;
}

export function deriveActivitySessionRules(
  activityId: string,
  metadata?: Pick<ActivityMetadata, "sessionConfig">,
): ActivitySessionRules {
  const normalizedId = toNormalizedActivityId(activityId);
  const config = metadata?.sessionConfig ?? DEFAULT_ACTIVITY_SESSION_CONFIG;

  const archetype = SESSION_ONLY_ACTIVITY_IDS.has(normalizedId)
    ? "session_only"
    : "scored_match";
  const fallbackResultSchema: ActivityResultSchema =
    COURT_MATCH_ACTIVITY_IDS.has(normalizedId)
      ? "sets"
      : TEAM_MATCH_ACTIVITY_IDS.has(normalizedId)
        ? "team_score"
        : "winner_only";
  const resultSchema =
    archetype === "scored_match"
      ? (config.resultSchema ?? fallbackResultSchema)
      : "none";

  return {
    ...DEFAULT_RULES,
    archetype,
    session: {
      joinable: true,
      formats: config.formats,
      defaultFormat: config.defaultFormat,
      participantRange: config.participantRange,
      supportsJoinApproval: true,
      supportsInviteCode: true,
      showsInFeed: true,
      showsInDiscover: true,
    },
    competition: {
      allowed: archetype === "scored_match" && config.supportsCompetition,
      modes:
        archetype === "scored_match" && config.supportsCompetition
          ? ["casual", "competitive"]
          : [],
      defaultMode:
        archetype === "scored_match" && config.supportsCompetition
          ? "casual"
          : null,
    },
    result: {
      allowed: archetype === "scored_match",
      optional: archetype === "scored_match",
      schema: resultSchema,
      verification: archetype === "scored_match" ? "participants" : "none",
      retroResultAllowed: archetype === "scored_match",
      affectsRatingWhen:
        archetype === "scored_match" ? "verified_competitive_only" : "never",
    },
    renderer: {
      family: getActivityRendererFamily(activityId),
      templateKey: getActivityTemplateKey(activityId),
      scoreEditorKey:
        archetype === "scored_match"
          ? getActivityTemplateKey(activityId)
          : null,
      scorecardKey:
        archetype === "scored_match"
          ? getActivityTemplateKey(activityId)
          : null,
    },
    profile: {
      modules: getProfileModules(archetype),
    },
  };
}

export function supportsSessionResults(
  activityId: string,
  metadata?: ActivityMetadata | null,
) {
  const rules = metadata
    ? deriveActivitySessionRules(activityId, metadata)
    : deriveActivitySessionRules(activityId);

  return rules.result.allowed;
}

export function supportsRetroResults(
  activityId: string,
  metadata?: ActivityMetadata | null,
) {
  const rules = metadata
    ? deriveActivitySessionRules(activityId, metadata)
    : deriveActivitySessionRules(activityId);

  return rules.result.retroResultAllowed;
}
